import { AppCacheStatus } from '@fullstory/redux-util';
import { useCallback, useEffect, useMemo } from 'react';
import { AppAction, AppDispatch } from '..';
import { useDispatch } from 'react-redux';
import { QueryCacheOptions } from '@fullstory/proto/fs/services/alexa';
import { AlexaRequestActions } from '../modules/alexa';

export const statusToResponsePriority = {
  [AppCacheStatus.Initial]: 0,
  [AppCacheStatus.Fresh]: 1,
  [AppCacheStatus.HasData]: 2,
  [AppCacheStatus.IsLoading]: 3,
  [AppCacheStatus.IsRefetching]: 3,
  [AppCacheStatus.LoadFailed]: 4,
};

/**
 * Minimal common computation response properties needed to reduce to single status values.
 */
type ComputationResponse<T> = {
  storageId: string;
  status: AppCacheStatus;
  safeMessage: string | null;
  data: T | null;
};

/**
 * Get the status of the whole set of computations
 */
export function useComputationSetStatus<T>(
  responses: ComputationResponse<T>[],
  getTimestamp: (d: T | undefined | null) => Date | null,
) {
  return useMemo(() => {
    const { status, data, safeMessage } = responses.reduce<{
      status: AppCacheStatus;
      data: ComputationResponse<T> | null;
      safeMessage: string | null;
    }>(
      (acc, response) =>
        statusToResponsePriority[response.status] > statusToResponsePriority[acc.status]
          ? {
              data: response,
              status: response.status,
              safeMessage: response.safeMessage,
            }
          : acc,
      { status: AppCacheStatus.Initial, data: null, safeMessage: null },
    );
    return {
      /**
       * The 'status' of the "key response"
       */
      status,
      /**
       * The 'safeMessage' of the "key response"
       */
      safeMessage,
      /**
       * The 'computedAt' timestamp of the "key response" as a `Date`
       */
      timestamp: getTimestamp(data?.data),
    };
  }, [responses, getTimestamp]);
}

export function useAppCacheSwapCallback<T>(
  responses: ComputationResponse<T>[],
  appCacheSwap: (storageId: string) => AppAction,
) {
  const dispatch = useDispatch<AppDispatch>();

  // The ResourceCacheButton shows the status with the highest priority (HasData > Fresh). Because of this we can get into a state where 1 or more
  // storage ids has new data in the app cache but the other storage ids have fresh data. If the user clicks the cache button, we only want to
  // swap the cache for the stale storage id. Swapping the cache for the other ids will inadvertantly wipe out the cached data.
  return useCallback(() => {
    responses.forEach(response => {
      if (response.status === AppCacheStatus.HasData) {
        dispatch(appCacheSwap(response.storageId));
      }
    });
  }, [appCacheSwap, dispatch, responses]);
}

/**
 * Releases possible background background refresh jobs in Alexa when computations
 * are no longer needed on the client (eg fall out of view).
 *
 * Note that this differs from in-flight request cancellation, which also should exist.
 * This is about releasing background refresh jobs that are no longer needed.
 * These jobs occur when a background recomputation is fired off _after_ returning an immediate cached result.
 *
 * @param orgId
 * @param backgroundRefIds collection from computation results
 * @param release if computations are no longer needed, set the release argument to true
 */
export const useReleaseAlexaBackgroundRefs = (
  orgId: string,
  backgroundRefIds: string[],
  release: boolean,
) => {
  const dispatch = useDispatch<AppDispatch>();

  const handleClearBackgroundRefs = () => {
    backgroundRefIds.forEach(backgroundRefId => {
      // short circuit if empty
      if (!backgroundRefId) return;

      dispatch(
        AlexaRequestActions.releaseBackgroundRef({
          orgId,
          backgroundRefId,
          queue: QueryCacheOptions.Queue.QUEUE_INVALID,
        }),
      );
    });
  };

  // background refs are cleared both when release is true, and on unmount
  // it will also fire when the release boolean changes values,
  // which should be okay both directions
  useEffect(() => {
    if (release) handleClearBackgroundRefs();
    return () => handleClearBackgroundRefs();
    // omitting handleClearBackgroundRefs dependency so changing background refs does not trigger
    // dont want to rely on stable references and we'd also only want to cleanup
    // backgroundRefId's that disappeared if the id's in the array changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [release]);
};
